import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import ChipValaszto from '../leadek/ChipValaszto.tsx';
import { idoszakKulcs, idoszakTartomany, IDOSZAK_CIMKE, ELOBEALLITASOK, type IdoszakKulcs } from '../leadek/idoszak.ts';
import LeadFokonyvSor, { ingatlanSzoveg, type LeadSor } from '../leadek/LeadFokonyvSor.tsx';
import { csvTartalom, fejMondat, napSzoveg, type CsvSor } from '../leadek/lista.ts';
import { FUL_STATUSZOK, OLDALMERET, szurokAlkalmazasa, szurokUrlbe, szurokUrlbol, type Ful, type Szurok } from '../leadek/szurok.ts';
import { useMost } from '../leadek/useMost.ts';
import { supabase } from '../lib/supabase.ts';
import { STATUSZ_CIMKE, TIPUS_CIMKE, type IngatlanTipus, type LeadStatusz } from '../lib/tipusok.ts';
import { kozosSkala } from '../ui/arSav.ts';
import CsoSav from '../ui/CsoSav.tsx';
import { Betolto, Fokonyv, Gomb, HibaUzenet, Mezo, OldalFej, Ures } from '../ui/elemek.tsx';

const TIPUSOK = Object.keys(TIPUS_CIMKE) as IngatlanTipus[];
const MINDEN_IRODA = 'minden';
const NYITOTT = FUL_STATUSZOK.nyitott;
const CSO_CIMKE: Record<string, string> = { uj: 'Új', elerve: 'Elérve', kesobb_aktualis: 'Később', szemle_egyeztetve: 'Szemle egyeztetve', szemle_megtortent: 'Szemle megvolt', megbizas: 'Megbízás' };
const FORRASOK = ['google', 'facebook', 'instagram'];
const EXPORT_LAP = 1000;
const EXPORT_MAX = 20_000;
const OSZLOPOK = 'id, office_id, created_at, telefon_ellenorizve_at, statusz, ingatlan_tipus, iranyitoszam, utca, hazszam, nev, telefon, pontszam, becsles_min, becsles_max, becsles_kozep, szakertoi_becsles, hivasi_idosav, assigned_to, ujrahivas_honap, alapterulet:becsles_bemenet->>alapterulet, terulet:teruletek(nev), kollega:profiles!leads_assigned_to_fkey(nev), iroda:offices(nev)';

const CHIP = 'inline-flex h-10 items-center gap-1.5 rounded-full border px-3.5 text-[13px] transition';
const CHIP_BE = 'border-vonal-eros bg-felulet text-szoveg';
const CHIP_KI = 'border-vonal text-szoveg-halk hover:text-szoveg';

function aktivSzuroSzam(s: Szurok): number {
  return [s.statusz, s.kereses, s.tipus, s.kollega, s.minPontszam !== null, s.forras, s.tol, s.ig, s.esedekes].filter(Boolean).length;
}

function fajlnev(): string {
  return `leadek-${new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Budapest' })}.csv`;
}

export default function LeadLista() {
  const { aktivId, jogok } = useAktivIroda();
  const [sp, setSp] = useSearchParams();
  // A lead törlése után az adatlap ide navigál; a jelzés a history-állapotban érkezik (frissítésre is megmarad, ezért elég egyszer látni).
  const torolve = (useLocation().state as { leadTorolve?: boolean } | null)?.leadTorolve === true;
  const s = szurokUrlbol(sp);
  const mindenIroda = jogok.super && sp.get(MINDEN_IRODA) === '1';
  const irodaId = mindenIroda ? null : aktivId;
  const nowMs = useMost(30_000);
  const [egyediNyitva, setEgyediNyitva] = useState(false);
  const [keresesSzoveg, setKeresesSzoveg] = useState(s.kereses ?? '');
  const [exportal, setExportal] = useState(false);
  const [exportHiba, setExportHiba] = useState<unknown>(null);
  const [exportKorlat, setExportKorlat] = useState(false);

  const urlSzoveg = (uj: Szurok, minden = mindenIroda) => {
    const p = szurokUrlbe(uj);
    if (minden) p.set(MINDEN_IRODA, '1');
    return p;
  };
  // A szűrő módosítása visszalép az első oldalra, a lapozás nem.
  const valtoztat = (resz: Partial<Szurok>) => setSp(urlSzoveg({ ...s, oldal: 0, ...resz }));

  // A keresés késleltetve kerül az URL-be (és így a lekérdezésbe); az URL külső változását visszaírjuk a mezőbe.
  useEffect(() => {
    const ertek = keresesSzoveg.trim() || null;
    if (ertek === s.kereses) return;
    const t = setTimeout(() => valtoztat({ kereses: ertek }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a `valtoztat` minden renderben új; elég a szöveg és az URL-érték változására futni.
  }, [keresesSzoveg, s.kereses]);
  useEffect(() => {
    setKeresesSzoveg((jelenlegi) => ((s.kereses ?? '') === jelenlegi.trim() ? jelenlegi : (s.kereses ?? '')));
  }, [s.kereses]);

  const kulcs = szurokUrlbe(s).toString();
  const lista = useQuery({
    queryKey: ['leadek', 'lista', irodaId, kulcs],
    queryFn: async () => {
      const q = supabase.from('leads')
        .select(OSZLOPOK, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(s.oldal * OLDALMERET, s.oldal * OLDALMERET + OLDALMERET - 1);
      const { data, count, error } = await szurokAlkalmazasa(q, s, irodaId);
      if (error) throw error;
      return { sorok: data as unknown as LeadSor[], osszes: count ?? 0 };
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // A sávmérő számai: a státuszválasztás, az esedékesség és az oldalszám nélküli szűrőkkel számolnak, hogy a szakaszok
  // egymás mellett összevethetők legyenek. Az esedékes újrahívások száma ugyanezekkel a szűrőkkel külön lekérdezés.
  const statuszok: LeadStatusz[] = [...NYITOTT, 'eladva', 'elveszett', ...(jogok.vezeto ? (['nem_ellenorzott'] as const) : [])];
  const darabok = useQuery({
    queryKey: ['leadek', 'darab', irodaId, szurokUrlbe({ ...s, ful: 'nyitott', statusz: null, esedekes: false, oldal: 0 }).toString(), jogok.vezeto],
    queryFn: async () => {
      const szamol = async (sz: Szurok) => {
        const q = supabase.from('leads').select('id', { count: 'exact', head: true });
        const { count, error } = await szurokAlkalmazasa(q, sz, irodaId);
        if (error) throw error;
        return count ?? 0;
      };
      const [esedekesDb, ...eredmeny] = await Promise.all([
        szamol({ ...s, ful: 'nyitott', statusz: null, esedekes: true, oldal: 0 }),
        ...statuszok.map(async (st) =>
          [st, await szamol({ ...s, ful: st === 'nem_ellenorzott' ? 'nem_ellenorzott' : 'mind', statusz: st, esedekes: false, oldal: 0 })] as const),
      ]);
      return { ...(Object.fromEntries(eredmeny) as Partial<Record<LeadStatusz, number>>), esedekes: esedekesDb as number };
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // Az oldalfej mondata az iroda összes új leadjéről szól (a szűrőktől függetlenül).
  const varakozok = useQuery({
    queryKey: ['leadek', 'varakozok', irodaId],
    queryFn: async () => {
      let q = supabase.from('leads').select('created_at, telefon_ellenorizve_at', { count: 'exact' }).eq('statusz', 'uj').limit(1000);
      if (irodaId) q = q.eq('office_id', irodaId);
      const { data, count, error } = await q;
      if (error) throw error;
      return { db: count ?? data.length, kezdetek: data.map((r) => Date.parse(r.telefon_ellenorizve_at ?? r.created_at)) };
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const slaIrodak = useQuery({
    queryKey: ['leadek', 'sla'],
    queryFn: async () => {
      const { data, error } = await supabase.from('offices').select('id, sla_perc');
      if (error) throw error;
      return new Map(data.map((o) => [o.id, o.sla_perc]));
    },
  });

  const kollegak = useQuery({
    queryKey: ['leadek', 'kollegak', aktivId],
    enabled: jogok.vezeto && aktivId !== null && !mindenIroda,
    queryFn: async () => {
      const { data, error } = await supabase.from('office_members')
        .select('user_id, profil:profiles(nev)').eq('office_id', aktivId!).eq('aktiv', true);
      if (error) throw error;
      return (data as unknown as { user_id: string; profil: { nev: string } | null }[])
        .map((t) => ({ id: t.user_id, nev: t.profil?.nev || 'Névtelen' }))
        .sort((a, b) => a.nev.localeCompare(b.nev, 'hu'));
    },
  });

  const sorok = lista.data?.sorok ?? [];
  const osszes = lista.data?.osszes ?? 0;
  const tol = s.oldal * OLDALMERET + 1;
  const ig = Math.min(osszes, tol + sorok.length - 1);
  const utolso = (s.oldal + 1) * OLDALMERET >= osszes;
  const szuroSzam = aktivSzuroSzam(s);

  const skala = useMemo(() => {
    const savok = sorok.flatMap((l) => (!l.szakertoi_becsles && l.becsles_min !== null && l.becsles_max !== null ? [{ min: l.becsles_min, max: l.becsles_max }] : []));
    return kozosSkala(savok);
  }, [sorok]);

  const d = darabok.data;
  const nyitottOsszes = NYITOTT.reduce((o, st) => o + (d?.[st] ?? 0), 0);
  const lezartDb = (d?.eladva ?? 0) + (d?.elveszett ?? 0);
  const csoSzakaszok = NYITOTT.map((st) => ({ kulcs: st, db: d?.[st] ?? 0, cimke: CSO_CIMKE[st]! }));

  const legregebbiPerc = varakozok.data && varakozok.data.kezdetek.length > 0 ? (nowMs - Math.min(...varakozok.data.kezdetek)) / 60_000 : null;
  const leiras = varakozok.data
    ? `${napSzoveg(nowMs)} ${fejMondat({ ujDb: varakozok.data.db, legregebbiPerc })}`
    : napSzoveg(nowMs);

  const csoValaszt = (kulcsErtek: string) =>
    valtoztat({ ful: 'nyitott', statusz: s.statusz === kulcsErtek && s.ful === 'nyitott' ? null : (kulcsErtek as LeadStatusz), esedekes: false });

  const ulFulek: { kulcs: Ful; cim: string; db?: number }[] = [
    { kulcs: 'nyitott', cim: 'Nyitott', db: darabok.data ? nyitottOsszes : undefined },
    { kulcs: 'lezart', cim: 'Lezárt', db: darabok.data ? lezartDb : undefined },
    { kulcs: 'mind', cim: 'Mind' },
    ...(jogok.vezeto ? [{ kulcs: 'nem_ellenorzott' as Ful, cim: 'Nem ellenőrzött', db: d?.nem_ellenorzott }] : []),
  ];

  const idoszakErtek: IdoszakKulcs = egyediNyitva ? 'egyedi' : idoszakKulcs({ tol: s.tol, ig: s.ig }, nowMs);
  const idoszakValtas = (k: IdoszakKulcs) => {
    if (k === 'egyedi') { setEgyediNyitva(true); return; }
    setEgyediNyitva(false);
    valtoztat(idoszakTartomany(k, nowMs));
  };
  const forrasLista = s.forras && !FORRASOK.includes(s.forras) ? [...FORRASOK, s.forras] : FORRASOK;

  const exportalas = async () => {
    setExportHiba(null);
    setExportKorlat(false);
    if (osszes > EXPORT_MAX) { setExportKorlat(true); return; }
    setExportal(true);
    try {
      const mind: LeadSor[] = [];
      for (let kezdet = 0; kezdet < EXPORT_MAX; kezdet += EXPORT_LAP) {
        const q = supabase.from('leads').select(OSZLOPOK).order('created_at', { ascending: false }).order('id').range(kezdet, kezdet + EXPORT_LAP - 1);
        const { data, error } = await szurokAlkalmazasa(q, s, irodaId);
        if (error) throw error;
        mind.push(...(data as unknown as LeadSor[]));
        if (data.length < EXPORT_LAP) break;
      }
      const csvSorok: CsvSor[] = mind.map((l) => ({
        created_at: l.created_at, nev: l.nev, telefon: l.telefon, statusz: l.statusz, tipus: ingatlanSzoveg(l), terulet: l.terulet?.nev ?? null,
        cim: [l.iranyitoszam, [l.utca, l.hazszam].filter(Boolean).join(' ')].filter(Boolean).join(', '),
        becsles_min: l.becsles_min, becsles_max: l.becsles_max, szakertoi: l.szakertoi_becsles === true || l.becsles_min === null || l.becsles_max === null,
        pontszam: l.pontszam, felelos: l.kollega?.nev ?? null, iroda: l.iroda?.nev ?? null,
      }));
      const url = URL.createObjectURL(new Blob([csvTartalom(csvSorok, mindenIroda)], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fajlnev();
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportHiba(e);
    } finally {
      setExportal(false);
    }
  };

  const nincsSzuro = szuroSzam === 0;
  return (
    <div>
      <OldalFej cim="Leadek" leiras={leiras}>
        <Gomb valtozat="masodlagos" toltes={exportal} onClick={exportalas} disabled={osszes === 0} className="max-sm:hidden">Exportálás</Gomb>
      </OldalFej>
      {torolve && <p role="status" className="mb-4 rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">A lead törölve.</p>}
      <HibaUzenet hiba={exportHiba} />
      {exportKorlat && (
        <p role="alert" className="mb-4 rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">
          Az exportálható sorok száma legfeljebb {EXPORT_MAX.toLocaleString('hu-HU')}. Szűkítsd a listát (például időszakkal), és próbáld újra.
        </p>
      )}

      <section aria-label="Leadek státusz szerint" className="mb-7">
        <CsoSav nev="Nyitott leadek státusz szerint" szakaszok={csoSzakaszok} kivalasztott={s.ful === 'nyitott' ? s.statusz : null} onValaszt={csoValaszt} />
        <div className="mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-1 text-[13px] text-szoveg-halk">
          <span>{nyitottOsszes} nyitott lead</span>
          <button type="button" className="rounded-md text-left hover:text-szoveg" onClick={() => valtoztat({ ful: 'lezart', statusz: null })}>
            {lezartDb === 0 ? 'Még nincs lezárt lead' : `Lezárva: ${d?.eladva ?? 0} eladva, ${d?.elveszett ?? 0} elveszett`}
          </button>
        </div>
      </section>
      <HibaUzenet hiba={darabok.error} />

      <section aria-label="Szűrők" className="mb-3.5 flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Állapot" className="flex flex-wrap gap-2">
          {ulFulek.map((f) => (
            <button key={f.kulcs} type="button" aria-pressed={s.ful === f.kulcs} onClick={() => valtoztat({ ful: f.kulcs, statusz: null, esedekes: false })}
              className={`${CHIP} ${s.ful === f.kulcs ? CHIP_BE : CHIP_KI}`}>
              {f.cim}
              {f.db !== undefined && <span className="szam text-xs text-szoveg-halk">{f.db}</span>}
            </button>
          ))}
        </div>
        {(s.esedekes || (d?.esedekes ?? 0) > 0) && (
          <button type="button" aria-pressed={s.esedekes} onClick={() => valtoztat({ ful: 'nyitott', statusz: null, esedekes: !s.esedekes })}
            className={`${CHIP} ${s.esedekes ? CHIP_BE : CHIP_KI}`}>
            Esedékes újrahívás
            {d && <span className="szam text-xs text-szoveg-halk">{d.esedekes}</span>}
          </button>
        )}
        {s.statusz && s.ful === 'nyitott' && (
          <button type="button" onClick={() => valtoztat({ statusz: null })} className={`${CHIP} ${CHIP_BE}`} aria-label={`Állapotszűrő törlése: ${STATUSZ_CIMKE[s.statusz]}`}>
            {STATUSZ_CIMKE[s.statusz]}<span aria-hidden className="text-szoveg-halk">×</span>
          </button>
        )}
        <ChipValaszto label="Típus" aktiv={s.tipus !== null} value={s.tipus ?? ''} onChange={(e) => valtoztat({ tipus: (e.target.value || null) as IngatlanTipus | null })}>
          <option value="">Minden típus</option>
          {TIPUSOK.map((t) => <option key={t} value={t}>{TIPUS_CIMKE[t]}</option>)}
        </ChipValaszto>
        {jogok.vezeto && !mindenIroda && (
          <ChipValaszto label="Kolléga" aktiv={s.kollega !== null} value={s.kollega ?? ''} onChange={(e) => valtoztat({ kollega: e.target.value || null })}>
            <option value="">Minden kolléga</option>
            {(kollegak.data ?? []).map((k) => <option key={k.id} value={k.id}>{k.nev}</option>)}
          </ChipValaszto>
        )}
        <ChipValaszto label="Minimális pontszám" aktiv={s.minPontszam !== null} value={s.minPontszam === null ? '' : String(s.minPontszam)}
          onChange={(e) => valtoztat({ minPontszam: e.target.value === '' ? null : Number(e.target.value) })}>
          <option value="">Bármilyen pontszám</option>
          {[40, 60, 80].map((p) => <option key={p} value={p}>{p} pont felett</option>)}
        </ChipValaszto>
        <ChipValaszto label="Forrás" aktiv={s.forras !== null} value={s.forras ?? ''} onChange={(e) => valtoztat({ forras: e.target.value || null })}>
          <option value="">Minden forrás</option>
          {forrasLista.map((f) => <option key={f} value={f}>{f[0]!.toUpperCase() + f.slice(1)}</option>)}
        </ChipValaszto>
        <ChipValaszto label="Beérkezés ideje" aktiv={idoszakErtek !== 'barmikor'} value={idoszakErtek} onChange={(e) => idoszakValtas(e.target.value as IdoszakKulcs)}>
          {[...ELOBEALLITASOK, 'egyedi' as const].map((k) => <option key={k} value={k}>{IDOSZAK_CIMKE[k]}</option>)}
        </ChipValaszto>
        {jogok.super && (
          <label className={`${CHIP} cursor-pointer ${mindenIroda ? CHIP_BE : CHIP_KI}`}>
            <input type="checkbox" className="size-4 accent-kiemelt" checked={mindenIroda}
              onChange={(e) => setSp(urlSzoveg({ ...s, oldal: 0, kollega: null }, e.target.checked))} />
            Minden iroda
          </label>
        )}
        <input type="search" aria-label="Keresés" placeholder="Keresés név, telefon vagy cím alapján" value={keresesSzoveg}
          onChange={(e) => setKeresesSzoveg(e.target.value)}
          className="h-10 min-w-[200px] flex-1 rounded-full border border-vonal bg-hatter px-3.5 text-[max(16px,1rem)] md:text-[13px] text-szoveg placeholder:text-szoveg-halk" />
        {!nincsSzuro && (
          <Gomb valtozat="szoveg" className="rounded-full" onClick={() => {
            setEgyediNyitva(false);
            setKeresesSzoveg('');
            valtoztat({ statusz: null, kereses: null, tipus: null, kollega: null, minPontszam: null, forras: null, tol: null, ig: null, esedekes: false });
          }}>
            Szűrők törlése
          </Gomb>
        )}
      </section>

      {(idoszakErtek === 'egyedi') && (
        <div className="mb-3.5 grid gap-3 sm:max-w-md sm:grid-cols-2">
          <Mezo label="Beérkezett ettől" type="date" value={s.tol ?? ''} onChange={(e) => valtoztat({ tol: e.target.value || null })} />
          <Mezo label="Beérkezett eddig" type="date" value={s.ig ?? ''} onChange={(e) => valtoztat({ ig: e.target.value || null })} />
        </div>
      )}

      {s.ful === 'nem_ellenorzott' && (
        <p className="mb-3.5 rounded-xl border border-vonal bg-hatter px-3.5 py-2.5 text-sm text-szoveg-halk">
          Ezek az érdeklődők nem erősítették meg a telefonszámukat. A személyes adataikat 30 nap után automatikusan töröljük.
        </p>
      )}

      <HibaUzenet hiba={lista.error} />
      {lista.isPending ? <Betolto /> : lista.error && !lista.data ? null : sorok.length === 0 ? (
        s.ful === 'nyitott' && nincsSzuro
          ? <Ures cim="Nincs nyitott lead." szoveg="Az új leadek itt jelennek meg." />
          : <Ures cim="Nincs ilyen lead." szoveg="Próbálj szélesebb szűrést, vagy töröld a szűrőket." />
      ) : (
        <Fokonyv>
          <div aria-hidden className="grid grid-cols-[4px_minmax(190px,1.3fr)_minmax(170px,1fr)_minmax(200px,1.2fr)_150px_120px] gap-x-[18px] pt-3 pb-2.5 pr-5 text-xs text-szoveg-halk max-[1199px]:hidden">
            <span /><span>Érdeklődő</span><span>Ingatlan</span><span>Online sáv</span><span>Állapot</span><span />
          </div>
          <ul className="divide-y divide-vonal">
            {sorok.map((l) => (
              <LeadFokonyvSor key={l.id} lead={l} kozosSkala={skala} slaPerc={slaIrodak.data?.get(l.office_id)} nowMs={nowMs} irodaNevMutat={mindenIroda} />
            ))}
          </ul>
        </Fokonyv>
      )}

      {osszes > 0 && (
        <nav aria-label="Lapozás" className="mt-4 flex items-center justify-between gap-3">
          <Gomb valtozat="masodlagos" disabled={s.oldal === 0} onClick={() => setSp(urlSzoveg({ ...s, oldal: s.oldal - 1 }))}>Előző</Gomb>
          <span className="szam text-sm text-szoveg-halk">{tol}–{ig} / {osszes}</span>
          <Gomb valtozat="masodlagos" disabled={utolso} onClick={() => setSp(urlSzoveg({ ...s, oldal: s.oldal + 1 }))}>Következő</Gomb>
        </nav>
      )}
    </div>
  );
}
